import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { parseSubscriptionData } from '../utils/subscriptionUtils';
import config from '../config';

// Create the context
const SubscriptionContext = createContext();

// Custom hook to use the subscription context
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// Provider component
export const SubscriptionProvider = ({ children, unityAuthData }) => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  
  const API_BASE_URL = config.backendUrl;

  // Get auth headers for API requests
  const getAuthHeaders = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    let firebaseToken = '';
    
    if (currentUser) {
      try {
        // Always force a token refresh to ensure we have a fresh one
        firebaseToken = await currentUser.getIdToken(true);
        console.log('Got fresh Firebase token for subscription API request');
      } catch (error) {
        console.error('Error getting Firebase token:', error);
      }
    }
    
    // Only include standard headers, no cache-control to avoid CORS issues
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': firebaseToken ? `Bearer ${firebaseToken}` : '',
      'X-Unity-Token': unityAuthData?.accessToken || ''
    };
    
    return headers;
  };

  // Function to fetch subscription data
  const fetchSubscriptionData = async (forceRefresh = false) => {
    // Don't fetch if we don't have a playerId
    if (!unityAuthData?.playerId) {
      console.log('Cannot fetch subscription data: Missing player ID');
      return null;
    }
    
    // Don't fetch if we've already fetched recently (within last 30 seconds) and not forcing refresh
    const now = Date.now();
    if (
      !forceRefresh && 
      lastFetchTime && 
      now - lastFetchTime < 30000 && // 30 seconds
      subscriptionData !== null
    ) {
      console.log('Using cached subscription data (fetched within last 30s)');
      return subscriptionData;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching subscription data from API for player:', unityAuthData.playerId);
      
      const headers = await getAuthHeaders();
      
      // Add cache-busting timestamp to URL
      const cacheBusterUrl = `${API_BASE_URL}/subscription/${unityAuthData.playerId}?_t=${now}`;
      
      const response = await fetch(cacheBusterUrl, { headers });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch subscription data: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Subscription API response:', data);
      
      // Parse the subscription data
      const parsedData = parseSubscriptionData(data);
      
      // Update state
      setSubscriptionData(parsedData);
      setLastFetchTime(now);
      
      return parsedData;
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when unityAuthData changes
  useEffect(() => {
    if (unityAuthData?.playerId) {
      fetchSubscriptionData();
    }
  }, [unityAuthData?.playerId]);

  // The context value
  const value = {
    subscriptionData,
    loading,
    error,
    refreshSubscription: () => fetchSubscriptionData(true),
    lastFetchTime
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
